import { create } from "zustand";
import type { RemoteThreadDocument } from "@/components/custom/providers/customThreadListProvider";

// Shared in-flight flag for doc-attach network calls. Send must block until the
// flag clears — otherwise a fast user could submit before the POST lands and
// the turn would go out without the just-added doc in context.
type DocAttachState = {
  inFlight: boolean;
  begin: () => void;
  end: () => void;
};
export const useDocAttachStore = create<DocAttachState>((set) => ({
  inFlight: false,
  begin: () => set({ inFlight: true }),
  end: () => set({ inFlight: false }),
}));

// Buffer for docs picked on a thread that doesn't yet have a remoteId. Held here
// until ComposerDocumentsStrip sees the threadId transition from undefined/__LOCAL
// → real GUID, then flushed with a single POST /threads/{id}/documents. The
// optimistic chips are also reflected in selectedDocs so the user sees them
// immediately; this buffer is the source of truth for the server-side
// reconciliation call.
type PendingDocAttachState = {
  pending: RemoteThreadDocument[];
  addPending: (docs: RemoteThreadDocument[]) => void;
  clearPending: () => void;
};
export const usePendingDocAttachStore = create<PendingDocAttachState>((set) => ({
  pending: [],
  addPending: (docs) => set(state => {
    const seen = new Set(state.pending.map(d => d.docId));
    const merged = [...state.pending];
    for (const d of docs) {
      if (!seen.has(d.docId)) { merged.push(d); seen.add(d.docId); }
    }
    return { pending: merged };
  }),
  clearPending: () => set({ pending: [] }),
}));
